'use client';

import { FormEventHandler, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import styled from '@emotion/styled';
import Button from '../../../../components/common/button/Button';
import { getShopDineRelaxListUrl } from '../../use-cases/getShopDineRelaxListUrl';
import { ShopDineRelaxSearchBar } from './ShopDineRelaxSearchBar';
import { ArrowForwardIcon, Css, ITheme, Responsive } from '../../../../ui';
import { AirportCodes } from '../../../../types/airports';

const StyledForm = styled.form<{}, ITheme>`
	display: flex;
	gap: ${Css.pxToRem(16)};
	border: none;
	padding: 0;
	margin: 0;
	align-items: stretch;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		gap: ${Css.pxToRem(18)};
		align-items: flex-end;
	}
`;

const StyledButton = styled(Button)`
	height: ${Css.pxToRem(56)};
	white-space: nowrap;
	justify-content: center;
	width: fit-content;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
		height: ${Css.pxToRem(48)};
	}
`;

interface IShopDineRelaxSearchProps {
	params: { query?: string };
	setCurrentPageIndex: (currentPageIndex: number) => void;
	airportCode: AirportCodes;
}

export const ShopDineRelaxSearch = ({
	params,
	setCurrentPageIndex,
	airportCode,
}: IShopDineRelaxSearchProps) => {
	const router = useRouter();
	const [value, setValue] = useState<string>('');
	const [isInvalid, setIsInvalid] = useState<boolean>(false);

	useEffect(() => {
		// Update search value when the query parameter changes in the url
		setValue(params.query || '');
	}, [params.query]);

	const onSubmit: FormEventHandler = (event) => {
		event.preventDefault();
		if (!value) return setIsInvalid(true);
		return onQuerySubmit(value);
	};

	const onValueChange = (val: string) => {
		if (val) {
			setIsInvalid(false);
		}
		setValue(val);
	};

	const onQuerySubmit = (query: string) => {
		const url = getShopDineRelaxListUrl({ airportCode, query });
		router.push(url, { scroll: false });
		setCurrentPageIndex(0);
	};

	return (
		<StyledForm onSubmit={onSubmit}>
			<ShopDineRelaxSearchBar
				value={value}
				onValueChange={onValueChange}
				onQuerySubmit={onQuerySubmit}
				isInvalid={isInvalid}
			/>
			<StyledButton
				type="submit"
				icon={<ArrowForwardIcon aria-label="Search" />}
			>
				Search
			</StyledButton>
		</StyledForm>
	);
};
